import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { pathOr } from 'ramda';
import { useTranslation } from 'react-i18next';
import { useCustomRouteMatch } from './useCustomRouteMatch';
import { randomGameIdSelector } from '../redux/reducers/app/configuration';
import { NAMESPACES } from '../constants/namespaces';
import { NAMESPACE_SEPARATOR } from '../constants';
import {
  ROOT,
  SEARCH,
  FILTER,
  FAVORITES,
  COMMON_ERROR,
  CATEGORY_LIST,
  CATEGORY_DETAIL,
  PROVIDER_LIST,
  PROVIDER_DETAIL,
  GAME_DETAIL,
  BONUSES_LIST,
  BONUSES_HISTORY,
  BONUS_DETAIL,
  CAMPAIGN_DETAIL,
  THEME_LIST,
  THEME_DETAIL,
  BONUS_GAMES_WAGERING,
  BONUS_GAMES_COLLECTING,
  HALL_OF_FAME,
  HALL_OF_FAME_HISTORY,
  CATEGORY_DETAIL_PAGINATED,
  PROVIDER_DETAIL_PAGINATED,
  THEME_DETAIL_PAGINATED,
  PARAM_CATEGORY_ID,
  PARAM_PROVIDER_ID,
  PARAM_THEME_ID,
  PARAM_GAME_ID,
  RECENT_GAMES,
} from '../constants/routeTemplates';
import { useQueryGameInfo } from './API/useQueryGames';
import { useQueryProvider } from './API/useQueryProviders';
import { useQueryCategory } from './API/useQueryCategories';
import { useQueryTheme } from './API/useQueryThemes';

// Common pages

const getMainPageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.INITIAL}${NAMESPACE_SEPARATOR}Slots`);
};

const getFilterPageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Filters`);
};

const getSearchPageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Search`);
};

const getFavoritesPageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Favorites`);
};

const getBonusPageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Bonuses`);
};

const getRecentGamesPageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Recent games`);
};

const getHallOfFamePageTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Hall of fame`);
};

const getGameTitle = ({ translate, translationKey }) => {
  return translate(`${NAMESPACES.GAME}${NAMESPACE_SEPARATOR}${translationKey}`);
};

const getBonusGamesWageringTitle = ({ translate }) => {
  return translate(
    `${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Games for wagering`
  );
};

const getBonusGamesCollectingTitle = ({ translate }) => {
  return translate(
    `${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Games for collecting`
  );
};

// Providers

const getProviderListTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Providers`);
};

const getProviderTitle = ({ translate, translationKey }) => {
  return translate(
    `${NAMESPACES.PROVIDER}${NAMESPACE_SEPARATOR}${translationKey}`
  );
};

// Categories

const getCategoryListTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Categories`);
};

const getCategoryTitle = ({ translate, translationKey }) => {
  return translate(
    `${NAMESPACES.CATEGORY}${NAMESPACE_SEPARATOR}${translationKey}`
  );
};

// Themes

const getThemesListTitle = ({ translate }) => {
  return translate(`${NAMESPACES.PAGES}${NAMESPACE_SEPARATOR}Themes`);
};

const getThemeTitle = ({ translate, translationKey }) => {
  return translate(
    `${NAMESPACES.THEME}${NAMESPACE_SEPARATOR}${translationKey}`
  );
};

const getCommonErrorCode = ({ params }) => params.status;

const CONFIG = {
  [ROOT]: getMainPageTitle,
  [FILTER]: getFilterPageTitle,
  [SEARCH]: getSearchPageTitle,
  [FAVORITES]: getFavoritesPageTitle,
  // SEO pages
  [PROVIDER_LIST]: getProviderListTitle,
  [PROVIDER_DETAIL]: getProviderTitle,
  [PROVIDER_DETAIL_PAGINATED]: getProviderTitle,

  [CATEGORY_LIST]: getCategoryListTitle,
  [CATEGORY_DETAIL]: getCategoryTitle,
  [CATEGORY_DETAIL_PAGINATED]: getCategoryTitle,

  [THEME_LIST]: getThemesListTitle,
  [THEME_DETAIL]: getThemeTitle,
  [THEME_DETAIL_PAGINATED]: getThemeTitle,

  [RECENT_GAMES]: getRecentGamesPageTitle,
  // Bonuses pages
  [BONUSES_LIST]: getBonusPageTitle,
  [BONUS_DETAIL]: getBonusPageTitle,
  [CAMPAIGN_DETAIL]: getBonusPageTitle,
  [BONUSES_HISTORY]: getBonusPageTitle,
  // Hall of fame pages
  [HALL_OF_FAME]: getHallOfFamePageTitle,
  [HALL_OF_FAME_HISTORY]: getHallOfFamePageTitle,
  // Games for bonuses
  [BONUS_GAMES_WAGERING]: getBonusGamesWageringTitle,
  [BONUS_GAMES_COLLECTING]: getBonusGamesCollectingTitle,
  // Game pages
  [GAME_DETAIL]: getGameTitle,
  // Error pages
  [COMMON_ERROR]: getCommonErrorCode,
};

const REQUIRED_NAMESPACES = [
  NAMESPACES.INITIAL,
  NAMESPACES.PAGES,
  NAMESPACES.CATEGORY,
  NAMESPACES.PROVIDER,
  NAMESPACES.THEME,
  NAMESPACES.GAME,
];

const getDynamicPageTitle = (path, configMap = {}) => {
  if (path === GAME_DETAIL) {
    return pathOr('', [path, 'data', 'game', 'translationKey'], configMap);
  }
  return pathOr('', [path, 'data', 'translationKey'], configMap);
};

export const useTranslate = (namespace = NAMESPACES.INITIAL) => {
  const { t, ready } = useTranslation(namespace, {
    useSuspense: false,
  });

  // Return empty string for title if translations is not ready to hide translation keys from the user
  return useMemo(() => (ready ? t : () => ''), [ready, t]);
};

export const usePageTitle = () => {
  const randomGameId = useSelector(randomGameIdSelector);
  const { path, params } = useCustomRouteMatch();
  const translate = useTranslate(REQUIRED_NAMESPACES);

  const queryRealGame = useQueryGameInfo(params[PARAM_GAME_ID], {
    enabled:
      path === GAME_DETAIL &&
      !!params[PARAM_GAME_ID] &&
      params[PARAM_GAME_ID] !== randomGameId,
  });
  const queryCategory = useQueryCategory(params[PARAM_CATEGORY_ID], {
    enabled: path.includes(CATEGORY_DETAIL) && !!params[PARAM_CATEGORY_ID],
  });
  const queryProvider = useQueryProvider(params[PARAM_PROVIDER_ID], {
    enabled: path.includes(PROVIDER_DETAIL) && !!params[PARAM_PROVIDER_ID],
  });
  const queryTheme = useQueryTheme(params[PARAM_THEME_ID], {
    enabled: path.includes(THEME_DETAIL) && !!params[PARAM_THEME_ID],
  });

  const queryConfigMap = {
    [GAME_DETAIL]: queryRealGame,
    [CATEGORY_DETAIL]: queryCategory,
    [CATEGORY_DETAIL_PAGINATED]: queryCategory,
    [PROVIDER_DETAIL]: queryProvider,
    [PROVIDER_DETAIL_PAGINATED]: queryProvider,
    [THEME_DETAIL]: queryTheme,
    [THEME_DETAIL_PAGINATED]: queryTheme,
  };

  const translationKey = getDynamicPageTitle(path, queryConfigMap);

  const title = CONFIG[path]
    ? CONFIG[path]({ translate, translationKey, params })
    : '';

  return title;
};
